
import React, { FC, useContext, useEffect, useState } from 'react';
import { ModalWithTrash } from '@investoro/core';
import dayjs from 'dayjs';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import ChatbotContext from 'utils/context/ChatbotContext';
import { MessageResponseDtoMessageOwnerEnum } from 'utils/types/ChatbotModels';
import { CheckOpinionParamsOpinionTypeEnum } from 'utils/types/UserSettingsModels';

import Indicator from 'components/atom/Indicator';
import ChatbotConversation from 'components/common/Chatbot/ChatbotConversation';
import ChatbotOpinion from 'components/common/Chatbot/ChatbotOpinion';
import FiltersContentWrapper from 'components/common/FiltersContentWrapper';
import Icon, { IconEnum } from 'components/common/Icon';
import ResourcePopup from 'components/common/ResourcePopUp';
import V2HeaderSection from 'components/V2HeaderSection';

const Wrapper = styled.div`
    overflow: hidden;

    .active .accordion-card-content {
        row-gap: 10px;
    }
`;

const NewConversationButton = styled.div`
    display: flex;
    align-items: center;
    padding: 15px 15px 0 25px;
    gap: 25px;
    border-radius: var(--box-border-radius);
    cursor: pointer;
    background-color: var(--color-white);
    color: white;
    margin: 15px 0 35px 0;
    
    &:hover {
        opacity: 0.9;
    }
`;

const DrawerOverlay = styled.div<{ isOpen: boolean }>`
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 998;
    opacity: ${(props) => props.isOpen ? 1 : 0};
    visibility: ${(props) => props.isOpen ? 'visible' : 'hidden'};
    transition: opacity 0.3s;

    @media (max-width: 1100px) {
        display: block;
    }
`;

const DrawerContent = styled.div`
    padding: 20px;
`;

const DrawerContainer = styled.div<{ isOpen: boolean }>`
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    background: var(--color-white);
    z-index: 999;
    transform: translateX(${(props) => props.isOpen ? '0' : '-100%'});
    transition: transform 0.3s;
    overflow-y: auto;

    @media (max-width: 1100px) {
        display: block;
    }
`;

const DrawerHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--color-alt-second);
`;

const HeaderButtonsWrapper = styled.div`
    display: none;
    
    @media (max-width: 1100px) {
        display: flex;
        height: auto;
        align-items: center;
        justify-content: center;
    }
`;

const NewConversationHeaderButton = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    margin: 10px 0 0 15px;
`;

const HistoryButton = styled.button`
    cursor: pointer;
    color: var(--color-primary);
    font-weight: var(--font-weight);
    margin: 10px 0 0 15px;
    font-size: 15px;
    height: 50px;
    border: 2px solid var(--color-primary);
    border-radius: 10px;
    padding: 12px;

    @media (max-width: 1100px) {
        margin: 0;
    }
`;

const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    text-align: center;
    padding: 20px;
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    
    h2 {
        margin-bottom: 10px;
    }
    
    p {
        margin-bottom: 20px;
        color: var(--color-text-secondary);
    }
`;

const FiltersContainer = styled.div`
    margin: 20px 0 0 20px;
    height: calc(100vh - 220px);
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;

    &:hover {
        scrollbar-color: var(--color-alt-second-10) transparent;
    }

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
        background: var(--color-alt-second-10);
    }

    @media (max-width: 1100px) {
        display: none;
    }
`;

const ConversationsList = styled.div`
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    margin-bottom: 15px;
    overflow: hidden;
    padding: 3px;
`;

const ConversationItem = styled.div<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    padding: 15px;
    cursor: pointer;
    transition: background-color 0.2s;
    background-color: ${(props) => props.isActive ? 'var(--color-alt-second)' : 'transparent'};
    border-radius: var(--box-border-radius);
`;

const ConversationIcon = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    
    img {
        width: 20px;
        height: 20px;
    }
`;

const ConversationInfo = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ConversationTitle = styled.p`
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 4px;
    font-size: 14px;
    color: var(--color-primary);
`;

const ConversationDate = styled.p`
    font-size: 11px;
    color: var(--color-primary);
`;

const ConversationActions = styled.div`
    cursor: pointer;
`;

const ConversationHeader = styled.div`
    padding: 15px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background: var(--color-white);
    border-radius: var(--box-border-radius) var(--box-border-radius) 0 0;
    box-shadow: 0px 7px 6px 0px rgba(0, 0, 0, 0.06);
    z-index: 2;
`;

const HeaderContent = styled.div<{ isDarkMode: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    line-height: 20px;
    color: var(--color-primary);
`;

const PageHeaderIconBox = styled.div`
    width: 38px;
    height: 38px;
    min-width: 38px;
    background-color: var(--color-alt);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        filter: brightness(0) invert(1);
    }
`;

const PageHeaderTitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 12px;
    line-height: 1.3;
`;

const PageAiBadge = styled.span`
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--color-alt);
    margin-bottom: 1px;
`;

const PageOnlineDot = styled.span`
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: var(--color-success);
`;

const PageOnlineStatus = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--color-success);
    font-weight: 400;
`;

const ConversationContainer = styled.div<{ isFullPage?: boolean }>`
    background: var(--color-white);
    box-shadow: 0 0 40px 0 rgba(0, 0, 0, 0.10);
    border-radius: var(--box-border-radius);
    display: flex;
    flex-direction: column;
    margin: 20px;
    overflow: hidden;
    height: ${(props) => props.isFullPage ? 'calc(100vh - 220px)' : '100%'};
    
    @media (max-width: 1100px) {
        margin: 20px;
    }
`;

const DeleteButton = styled.div`
    margin-left: auto;
    cursor: pointer;
    padding: 8px;
    
    &:hover {
        opacity: 0.7;
    }
`;

const DeleteConfirmation = styled.div`
    background-color: var(--color-alt-second);
    padding: 10px;
    margin-top: 5px;
    border-radius: var(--box-border-radius);
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const DeleteConfirmationButtons = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
    
    button {
        flex: 1;
        padding: 8px;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-weight: 500;
        
        &.cancel {
            background-color: var(--color-alt-second);
            color: var(--color-primary);
        }
        
        &.confirm {
            background-color: var(--color-error);
            color: white;
        }
    }
`;

const ChatbotMessagesPage: FC = () => {
    const { user } = useUser();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
    const [hasUserOpinion, setHasUserOpinion] = useState<boolean | null>(null);

    useEffect(() => {
        checkUserOpinion();
    }, []);

    const checkUserOpinion = async () => {
        const opinonExist = await userApiRequests.checkOpinion(CheckOpinionParamsOpinionTypeEnum.CHAT_BOT);
        setHasUserOpinion(opinonExist);
    };

    const handleOpinionSubmitted = async () => {
        await checkUserOpinion();
    };

    const {
        activeConversation,
        conversationHistory,
        selectedConversationUuid,
        isLoading,
        isLoadingMessage,
        message,
        setMessage,
        sendMessageInHistory,
        loadConversationHistory,
        loadConversationDetails,
        startNewConversation,
        deleteConversation,
        isOpenResourcePopUp,
        closeResourcePopUp,
        getMessageAudio

    } = useContext(ChatbotContext);

    useEffect(() => {
        loadConversationHistory();
    }, []);

    const truncateTitle = (title: string, maxLength = 25) => {
        if (title.length <= maxLength) return title;

        return title.substring(0, maxLength) + '...';
    };

    const ConversationHistoryMenuButton = () => <HeaderButtonsWrapper>
        <HistoryButton onClick={() => setIsDrawerOpen(true)}>
            Historia konwersacji
        </HistoryButton>
        <NewConversationHeaderButton onClick={() => startNewConversation()}>+ chat</NewConversationHeaderButton>
    </HeaderButtonsWrapper>;

    return (
        <Wrapper id={'offer-list'}>
            <V2HeaderSection title="Flipchat" chatbotButton={<ConversationHistoryMenuButton/>} opinionChatContainer={
                hasUserOpinion === false ? <ChatbotOpinion onOpinionSubmitted={handleOpinionSubmitted} /> : undefined
            } />
            <DrawerOverlay isOpen={isDrawerOpen} onClick={() => setIsDrawerOpen(false)} />

            <DrawerContainer isOpen={isDrawerOpen}>
                <DrawerHeader>
                    <span>Historia konwersacji</span>
                    <Icon
                        icon={IconEnum.CROSS}
                        onClick={() => setIsDrawerOpen(false)}
                    />
                </DrawerHeader>

                <DrawerContent>
                    <NewConversationButton onClick={() => {
                        startNewConversation();
                        setIsDrawerOpen(false);
                    }}>
                        <Icon icon={IconEnum.PLUS} />
                        <span>Nowy chat</span>
                    </NewConversationButton>

                    {conversationHistory?.oldestChat && conversationHistory.oldestChat.length > 0
                        ? <ConversationsList>
                            {conversationHistory.oldestChat.map((chat) => <React.Fragment key={chat.conversationUuid}>
                                <ConversationItem
                                    isActive={selectedConversationUuid === chat.conversationUuid}
                                >
                                    <ConversationIcon
                                        onClick={() => {
                                            loadConversationDetails(chat.conversationUuid);
                                            setIsDrawerOpen(false);
                                        }}>
                                        <Icon icon={IconEnum.AI_STARS} />
                                    </ConversationIcon>
                                    <ConversationInfo
                                        onClick={() => {
                                            loadConversationDetails(chat.conversationUuid);
                                            setIsDrawerOpen(false);
                                        }}>
                                        <ConversationTitle>{truncateTitle(chat.title)}</ConversationTitle>
                                        <ConversationDate>
                                            {dayjs(chat.createdAt).format('DD.MM.YYYY HH:mm')}
                                        </ConversationDate>
                                    </ConversationInfo>

                                    <DeleteButton onClick={() => setConversationToDelete(chat.conversationUuid)}>
                                        <Icon icon={IconEnum.DELETE} />
                                    </DeleteButton>
                                </ConversationItem>

                                {conversationToDelete === chat.conversationUuid &&
                            <DeleteConfirmation>
                                <p>Czy na pewno chcesz usunąć tę konwersację?</p>
                                <DeleteConfirmationButtons>
                                    <button
                                        className="cancel"
                                        onClick={() => setConversationToDelete(null)}
                                    >
                                        Anuluj
                                    </button>
                                    <button
                                        className="confirm"
                                        onClick={() => {
                                            deleteConversation(chat.conversationUuid);
                                            setConversationToDelete(null);
                                        }}
                                    >
                                        Usuń
                                    </button>
                                </DeleteConfirmationButtons>
                            </DeleteConfirmation>
                                }
                            </React.Fragment>)}
                        </ConversationsList>
                        : <EmptyStateContainer>
                            <h2>Brak historii konwersacji</h2>
                            <p>Rozpocznij nową konwersację</p>
                        </EmptyStateContainer>
                    }
                </DrawerContent>
            </DrawerContainer>

            <FiltersContentWrapper className={'active'}>
                <FiltersContainer>
                    {conversationHistory?.oldestChat && conversationHistory.oldestChat.length > 0
                        ? <ConversationsList>
                            <NewConversationButton onClick={startNewConversation}>
                                <Icon icon={IconEnum.PLUS} />
                                <span>Nowy  chat</span>
                            </NewConversationButton>
                            {conversationHistory.oldestChat.map((chat) => <ConversationItem
                                key={chat.conversationUuid}
                                isActive={selectedConversationUuid === chat.conversationUuid}
                            >
                                <ConversationIcon
                                    onClick={() => loadConversationDetails(chat.conversationUuid)}>
                                    <Icon icon={IconEnum.AI_STARS} />
                                </ConversationIcon>
                                <ConversationInfo
                                    onClick={() => loadConversationDetails(chat.conversationUuid)}>
                                    <ConversationTitle>{truncateTitle(chat.title)}</ConversationTitle>
                                    <ConversationDate>{dayjs(chat.createdAt).format('DD.MM.YYYY HH:mm')}</ConversationDate>
                                </ConversationInfo>

                                <ConversationActions>
                                    <ModalWithTrash
                                        onChange={() => deleteConversation(chat.conversationUuid)}
                                        filter={chat.title}
                                        title="Usuń konwersację"
                                        description="Czy na pewno chcesz usunąć tę konwersację?"
                                        isDarkMode={user.isDarkMode}
                                    />
                                </ConversationActions>
                            </ConversationItem>)}
                        </ConversationsList>
                        : <EmptyStateContainer>
                            <h2>Brak historii konwersacji</h2>
                            <p>Rozpocznij nową konwersację</p>
                        </EmptyStateContainer>
                    }
                </FiltersContainer>

                <ConversationContainer isFullPage={true}>
                    <ConversationHeader>
                        <HeaderContent isDarkMode={user.isDarkMode}>
                            <PageHeaderIconBox>
                                <Icon icon={IconEnum.AI_STARS} />
                            </PageHeaderIconBox>
                            <PageHeaderTitleGroup>
                                <PageAiBadge>Investoro AI</PageAiBadge>
                                <p>{activeConversation ? activeConversation.title : 'Nowa konwersacja'}</p>
                                <PageOnlineStatus>
                                    <PageOnlineDot />
                                    Online
                                </PageOnlineStatus>
                            </PageHeaderTitleGroup>
                        </HeaderContent>
                    </ConversationHeader>

                    {activeConversation
                        ? <ChatbotConversation
                            messages={activeConversation.messages}
                            isLoadingMessage={isLoadingMessage}
                            message={message}
                            setMessage={setMessage}
                            onSendMessage={() => sendMessageInHistory(message)}
                            isDarkMode={user.isDarkMode}
                            isFullPage={true}
                            onGetMessageAudio={getMessageAudio}
                        />
                        : <ChatbotConversation
                            isFullPage={true}
                            maxRows={8}
                            messages={[
                                {
                                    message: 'Cześć! Odpowiem na każde pytanie o flipowaniu, aplikacji i stworzę Ci filtry w apce – wystarczy, że napiszesz, czego potrzebujesz.',
                                    messageOwner: MessageResponseDtoMessageOwnerEnum.BOT_MESSAGE,
                                    createdAt: new Date().toISOString()
                                }
                            ]}
                            isLoadingMessage={isLoadingMessage}
                            message={message}
                            setMessage={setMessage}
                            onSendMessage={() => sendMessageInHistory(message)}
                            isDarkMode={user.isDarkMode}
                            onGetMessageAudio={getMessageAudio}
                        />
                    }
                </ConversationContainer>
                <ResourcePopup
                    isOpen={isOpenResourcePopUp}
                    availableResource={{ smsCount: null, tokenCount: 0 }}
                    onClose={closeResourcePopUp} />

            </FiltersContentWrapper>
            {isLoading && <Indicator/>}
        </Wrapper>
    );
};

export default ChatbotMessagesPage;
